/**
 * Sparkline - Lightweight SVG sparkline chart component
 * 
 * Renders a simple line chart from an array of numeric values.
 * Supports customization of size, colors, and trend-based coloring.
 */

import React from 'react';

const Sparkline = ({
  data = [],
  width = 100,
  height = 30,
  strokeWidth = 1.5,
  color = null, // If null, auto-determines based on trend
  showDot = true, // Show dot at the last point
  className = '',
}) => {
  // Need at least 2 points to draw a line
  if (!data || data.length < 2) {
    return (
      <svg width={width} height={height} className={className}>
        <text x={width / 2} y={height / 2} textAnchor="middle" fontSize="10" fill="#9ca3af">
          --
        </text>
      </svg>
    );
  }

  // Calculate min/max for scaling
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1; // Avoid division by zero

  // Add padding so line doesn't touch edges
  const padding = 4;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  // Convert data points to SVG coordinates
  const points = data.map((value, index) => {
    const x = padding + (index / (data.length - 1)) * chartWidth;
    const y = padding + chartHeight - ((value - min) / range) * chartHeight;
    return { x, y, value };
  });

  // Create path string
  const pathD = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
    .join(' ');

  // Determine color based on trend (first vs last value)
  const firstValue = data[0];
  const lastValue = data[data.length - 1];
  const trendColor = lastValue >= firstValue ? '#10b981' : '#ef4444'; // green or red
  const lineColor = color || trendColor;

  // Last point for dot
  const lastPoint = points[points.length - 1];

  return (
    <svg 
      width={width} 
      height={height} 
      className={className}
      style={{ display: 'block' }}
    >
      {/* Gradient for fill under line */}
      <defs>
        <linearGradient id={`sparkline-gradient-${lineColor.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lineColor} stopOpacity="0.2" />
          <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Fill area under line */}
      <path
        d={`${pathD} L ${lastPoint.x.toFixed(2)} ${height - padding} L ${padding} ${height - padding} Z`}
        fill={`url(#sparkline-gradient-${lineColor.replace('#', '')})`}
      />

      {/* Main line */}
      <path
        d={pathD}
        fill="none"
        stroke={lineColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Dot at last point */}
      {showDot && (
        <circle
          cx={lastPoint.x}
          cy={lastPoint.y}
          r={3}
          fill={lineColor}
        />
      )}
    </svg>
  );
};

export default Sparkline;
